import React, { useRef } from 'react';
import { cx } from './cx';
import { focusRing } from './focusRing';
import { playClickSound } from '@/utils/soundUtils';

export interface SegmentItem<T extends string> {
  value: T;
  label: React.ReactNode;
  /** e.g. a CountBadge. */
  badge?: React.ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  items: SegmentItem<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  /** `lg`: 48 design px segments, for touch-first hub screens. */
  size?: 'sm' | 'md' | 'lg';
  /** Blocks every segment (e.g. while a request is in flight). */
  disabled?: boolean;
  className?: string;
}

/** Two to four mutually exclusive views; arrows move the selection. Segments take the button corners. */
export default function SegmentedControl<T extends string>({ items, value, onChange, 'aria-label': ariaLabel, size = 'md', disabled = false, className }: SegmentedControlProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const index = Math.max(0, items.findIndex((i) => i.value === value));
  const move = (to: number) => {
    const n = (to + items.length) % items.length;
    onChange(items[n].value);
    refs.current[n]?.focus();
  };
  return (
    <div role="tablist" aria-label={ariaLabel} className={cx('grid auto-cols-fr grid-flow-col gap-1 rounded-container border border-edge-subtle bg-inset p-1', className)}>
      {items.map((item, i) => {
        const selected = i === index;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              if (!selected) playClickSound();
              onChange(item.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(i + 1); }
              else if (e.key === 'ArrowLeft') { e.preventDefault(); move(i - 1); }
              else if (e.key === 'Home') { e.preventDefault(); move(0); }
              else if (e.key === 'End') { e.preventDefault(); move(items.length - 1); }
            }}
            className={cx(
              'relative inline-flex items-center justify-center gap-2 rounded-button font-ui font-semibold transition-colors duration-(--k-dur-fast)',
              size === 'sm' ? 'h-8 px-2 text-small' : size === 'lg' ? 'h-12 px-3 text-body' : 'h-9 px-2.5 text-small',
              focusRing,
              'disabled:cursor-not-allowed',
              selected ? 'bg-selected text-primary' : 'text-secondary enabled:hover:text-primary disabled:text-disabled'
            )}
          >
            <span className="truncate">{item.label}</span>
            {item.badge}
            {selected ? <span aria-hidden className="absolute inset-x-[22%] bottom-0.5 h-0.5 rounded-full bg-beam" /> : null}
          </button>
        );
      })}
    </div>
  );
}
